import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PracticeAreasPreview = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    setCurrentLanguage(savedLanguage);
  }, []);

  const practiceAreas = [
    {
      id: 'corporate-law',
      title: currentLanguage === 'en' ? 'Corporate Law' : 'Sheria ya Makampuni',
      description: currentLanguage === 'en' ?'Comprehensive business legal services including company formation, contracts, mergers & acquisitions, and regulatory compliance for businesses of all sizes.' :'Huduma kamili za kisheria za biashara ikiwa ni pamoja na uundaji wa makampuni, mikataba, muungano na ununuzi, na kufuata kanuni za biashara za ukubwa wote.',
      icon: 'Building2',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      features: currentLanguage === 'en' 
        ? ['Company Registration', 'Contract Drafting', 'Compliance Advisory', 'M&A Support']
        : ['Usajili wa Kampuni', 'Kuandika Mikataba', 'Ushauri wa Kufuata Sheria', 'Msaada wa M&A'],
      color: 'primary'
    },
    {
      id: 'family-law',
      title: currentLanguage === 'en' ? 'Family Law' : 'Sheria ya Familia',
      description: currentLanguage === 'en' ?'Sensitive handling of family matters including divorce proceedings, child custody, matrimonial property disputes, and adoption processes.' :'Kushughulikia kwa uangalifu mambo ya kifamilia ikiwa ni pamoja na michakato ya talaka, ulinzi wa watoto, migogoro ya mali ya ndoa, na michakato ya kulea.',
      icon: 'Heart',
      image: 'https://images.unsplash.com/photo-1609220136736-443140cffec6?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      features: currentLanguage === 'en'
        ? ['Divorce Proceedings', 'Child Custody', 'Property Division', 'Adoption Services']
        : ['Michakato ya Talaka', 'Ulinzi wa Watoto', 'Mgawanyo wa Mali', 'Huduma za Kulea'],
      color: 'accent'
    },
    {
      id: 'property-law',
      title: currentLanguage === 'en' ? 'Property Law' : 'Sheria ya Mali',
      description: currentLanguage === 'en' ?'Expert guidance on real estate transactions, land disputes, property development, and title transfers with thorough due diligence.' :'Uongozi wa kitaalamu juu ya miamala ya mali, migogoro ya ardhi, maendeleo ya mali, na uhamisho wa hati kwa uchunguzi wa kina.',
      icon: 'Home',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      features: currentLanguage === 'en'
        ? ['Property Transactions', 'Land Disputes', 'Title Verification', 'Development Law']
        : ['Miamala ya Mali', 'Migogoro ya Ardhi', 'Uthibitisho wa Hati', 'Sheria ya Maendeleo'],
      color: 'success'
    }
  ];

  const content = {
    en: {
      sectionTitle: "Our Practice Areas",
      sectionSubtitle: "Comprehensive Legal Services Tailored to Your Needs",
      viewAllButton: "View All Practice Areas",
      learnMoreButton: "Learn More"
    },
    sw: {
      sectionTitle: "Maeneo Yetu ya Utendaji",
      sectionSubtitle: "Huduma Kamili za Kisheria Zilizobinafsishwa kwa Mahitaji Yako",
      viewAllButton: "Ona Maeneo Yote ya Utendaji",
      learnMoreButton: "Jifunze Zaidi"
    }
  };

  const currentContent = content[currentLanguage];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full">
              <Icon name="Scale" size={20} className="text-primary" />
              <span className="text-sm font-medium text-primary">
                {currentLanguage === 'en' ? 'Legal Expertise' : 'Utaalamu wa Kisheria'}
              </span>
            </div>
          </div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-text-primary mb-6">
            {currentContent.sectionTitle}
          </h2>
          
          <p className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            {currentContent.sectionSubtitle}
          </p>
        </div>

        {/* Practice Areas Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {practiceAreas.map((area) => (
            <div key={area.id} className="group card-elevated hover:shadow-lg transition-all duration-300">
              {/* Image */}
              <div className="relative overflow-hidden rounded-t-lg">
                <Image
                  src={area.image}
                  alt={`${area.title} legal services`}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  <div className={`flex items-center justify-center w-12 h-12 bg-${area.color} rounded-lg shadow-lg`}>
                    <Icon name={area.icon} size={24} className="text-white" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-heading font-semibold text-text-primary mb-3">
                  {area.title}
                </h3>
                
                <p className="text-text-secondary mb-4 leading-relaxed">
                  {area.description}
                </p>

                {/* Features */}
                <div className="grid grid-cols-2 gap-2 mb-6">
                  {area.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Icon name="Check" size={16} className="text-success flex-shrink-0" />
                      <span className="text-sm text-text-secondary">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA Button */}
                <Link to={`/individual-practice-area-detail?area=${area.id}`}>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all duration-300"
                  >
                    {currentContent.learnMoreButton}
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Link to="/practice-areas-overview">
            <Button
              variant="primary"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
            >
              {currentContent.viewAllButton}
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PracticeAreasPreview;